import React from 'react';
import { motion } from 'framer-motion';
import { color, radius, TOUCH } from '../tokens';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'md' | 'lg';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  loading?: boolean;
  leadingIcon?: React.ReactNode;
}

function variantStyle(variant: ButtonVariant): React.CSSProperties {
  switch (variant) {
    case 'primary':
      return { background: color.accent, color: color.accentText, border: '1px solid transparent' };
    case 'destructive':
      return { background: color.danger, color: '#fff', border: '1px solid transparent' };
    case 'secondary':
      return { background: color.surfaceRaised, color: color.text, border: `1px solid ${color.border}` };
    case 'ghost':
    default:
      return { background: 'transparent', color: color.text, border: '1px solid transparent' };
  }
}

const labelStyle: React.CSSProperties = { display: 'inline-flex', alignItems: 'center' };
const springTransition = { type: 'spring' as const, stiffness: 500, damping: 30 };

/**
 * Primary action button for the redesign. Token-driven (theme-aware), with a
 * thumb-first minimum height and a tactile press animation.
 */
export default function Button({
  children, variant = 'primary', size = 'md', fullWidth, loading, leadingIcon, disabled, style, ...rest
}: ButtonProps) {
  const minH = size === 'lg' ? 52 : TOUCH;
  const tap = { scale: disabled || loading ? 1 : 0.97 };
  const btnStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: minH,
    width: fullWidth ? '100%' : undefined,
    padding: size === 'lg' ? '0 22px' : '0 18px',
    borderRadius: radius.md,
    fontSize: size === 'lg' ? 16 : 15,
    fontWeight: 600,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    transition: 'background 0.15s, opacity 0.15s',
    WebkitTapHighlightColor: 'transparent',
    ...variantStyle(variant),
    ...style,
  };
  return (
    <motion.button
      {...(rest as object)}
      whileTap={tap}
      transition={springTransition}
      disabled={disabled || loading}
      style={btnStyle}
    >
      {leadingIcon}
      <span style={labelStyle}>{loading ? '…' : children}</span>
    </motion.button>
  );
}
